import { ReactNode } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { Box, Heading, Stack, Text } from '@chakra-ui/react';

import { Alert, AlertMessage } from '~/components/alert';
import { SectionCard } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { LoadingOverlay } from '~/components/loading-overlay';
import { TemplateForm } from '~/components/template-form';
import { publicEnv } from '~/features/environment/public';
import { TemplateForm as TemplateFormType } from '~/features/page-template/models/template-form';
import { SelectOption } from '~/types/select-option';

import type { WhiteCardFormTemplate } from './model';

export function WhiteCardForm({
  whiteCardFormTemplate,
  formError,
  isCaptchaRequired,
  isLoading,
  isSubmitting,
  setCaptchaValue,
  defaultValues,
  onFormSubmit,
  onCancelClick,
  renderFormFields,
  isInputsFullWidth = false,
}: {
  whiteCardFormTemplate: WhiteCardFormTemplate;
  formError?: AlertMessage;
  isCaptchaRequired?: boolean;
  isLoading?: boolean;
  isSubmitting?: boolean;
  setCaptchaValue?: (value: string) => void;
  defaultValues?: Record<string, string | boolean | SelectOption>;
  onFormSubmit?: ({ templateForm, formData }: { templateForm: TemplateFormType; formData: any }) => void;
  onCancelClick?: () => void;
  renderFormFields?: (formFields: ReactNode[]) => ReactNode;
  isInputsFullWidth?: boolean;
}) {
  return (
    <ContentContainer>
      <SectionCard>
        <Stack spacing={'40px'}>
          <Stack spacing={'16px'}>
            {whiteCardFormTemplate.glyphName && <Glyph height={64} name={whiteCardFormTemplate.glyphName} width={64} />}
            <Stack spacing={'8px'}>
              <Heading as='h1' size='h1'>
                {whiteCardFormTemplate.pageTitle}
              </Heading>
              {whiteCardFormTemplate.subtitle && <Text>{whiteCardFormTemplate.subtitle}</Text>}
            </Stack>
          </Stack>
          {formError && <Alert status={formError.level}>{formError.message}</Alert>}
          {isCaptchaRequired && setCaptchaValue && (
            <>
              <ReCAPTCHA
                sitekey={publicEnv.NEXT_PUBLIC_USER_API_RECAPTCHA_KEY}
                size='normal'
                onChange={(value) => {
                  if (value) {
                    setCaptchaValue(value);
                  }
                }}
              />
            </>
          )}
          <Box position='relative'>
            {isLoading && <LoadingOverlay message='Creating your offer...' />}
            <TemplateForm
              defaultValues={defaultValues}
              isFullWidth={isInputsFullWidth}
              renderFormFields={renderFormFields}
              templateForm={whiteCardFormTemplate.form}
              submitButtonProps={{
                isLoading: isSubmitting,
              }}
              onCancelClick={onCancelClick}
              onSubmit={onFormSubmit}
            />
          </Box>
        </Stack>
      </SectionCard>
    </ContentContainer>
  );
}
